import React, { useState, useEffect, useRef } from 'react';
import { FormModal, Button, SelectInput, FormField } from '../ui';
import { InventoryItem, Currency } from '../../types/item';
import { ButtonVariant, BarcodeTemplate } from '../../types/ui';
// Import the new utility and getStandardBarcodePrintStyles
import { getStandardBarcodePrintStyles, generateDataMatrixSVG, generatePrintableLabelHtml } from '../../utils/barcodeUtils';
import BarcodeLabel from './BarcodeLabel';
import { getCurrentExchangeRate } from '../../services/exchangeRateService';

interface BarcodePrintModalProps {
  isOpen: boolean;
  onClose: () => void;
  barcode: string;
  item?: InventoryItem;
  template?: BarcodeTemplate;
}

/**
 * Modal for printing barcode labels
 * Handles the print workflow and provides a preview of the barcode label
 * Allows selection of different barcode templates
 */
const BarcodePrintModal: React.FC<BarcodePrintModalProps> = ({
  isOpen,
  onClose,
  barcode,
  item,
  template
}) => {
  const [isPrinting, setIsPrinting] = useState(false);
  const [barcodeReady, setBarcodeReady] = useState(false);
  const barcodeReadyRef = useRef(false);
  const [selectedTemplate, setSelectedTemplate] = useState<BarcodeTemplate>(template || BarcodeTemplate.A);
  const [currentExchangeRate, setCurrentExchangeRate] = useState<number>(0);
  const [dataMatrixSvg, setDataMatrixSvg] = useState<string>('');
  const [generatingSvg, setGeneratingSvg] = useState<boolean>(false);

  // Reset state when modal opens/closes or template changes
  useEffect(() => {
    if (isOpen) {
      setBarcodeReady(false);
      barcodeReadyRef.current = false;
      setIsPrinting(false);
      
      // Update selected template if provided template changes
      if (template) {
        setSelectedTemplate(template);
      }
    }
  }, [isOpen, template]);

  // Ensure barcodeReady state is updated
  useEffect(() => {
    // Check if barcodes are rendered
    const checkBarcodes = () => {
      const svgElements = document.querySelectorAll('.barcode-label-container svg');
      if (svgElements && svgElements.length >= 1) {
        const hasContent = Array.from(svgElements).every(svg => 
          svg.querySelector('rect') || svg.querySelector('g')
        );
        
        if (hasContent && !barcodeReadyRef.current) {
          barcodeReadyRef.current = true;
          setBarcodeReady(true);
        } else if (!hasContent && barcodeReady) {
          setBarcodeReady(false);
        }
      } else {
        // console.log('[PrintModal] useEffect[isOpen, barcodeReady] - No SVG elements found yet.'); // Optional: Log no SVG found
      }
    };
    
    if (isOpen) {
      const interval = setInterval(checkBarcodes, 500);
      return () => {
        clearInterval(interval);
      };
    }
  }, [isOpen, barcodeReady]);

  // Fetch current exchange rate when needed for Template D or new Template A
  useEffect(() => {
    if (
      isOpen &&
      (selectedTemplate === BarcodeTemplate.D || selectedTemplate === BarcodeTemplate.A) && // Added Template A
      item?.sellCurrency === 'TRY'
    ) {
      getCurrentExchangeRate().then(rate => {
        setCurrentExchangeRate(rate);
      }).catch(err => {
        console.error('Error fetching exchange rate:', err);
      });
    }
  }, [isOpen, selectedTemplate, item]); // Added isOpen to dependencies

  // Generate Data Matrix SVG when barcode changes
  useEffect(() => {
    if (isOpen && barcode) {
      setGeneratingSvg(true);
      setDataMatrixSvg('');
      generateDataMatrixSVG(barcode, 7.2)
        .then(svg => {
          setDataMatrixSvg(svg);
        })
        .catch(err => {
          console.error("[PrintModal] useEffect[isOpen, barcode] - Failed to generate Data Matrix SVG:", err);
        })
        .finally(() => {
          setGeneratingSvg(false);
        });
    }
  }, [isOpen, barcode]);

  // Handle when onLoad callback is triggered from BarcodeLabel
  const handleBarcodeLoad = () => {
    barcodeReadyRef.current = true;
    setBarcodeReady(true);
  };

  // Get template options for select dropdown
  const getTemplateOptions = () => [
    { label: 'Şablon A - Ön DM+ID, Arka USD Fiyat', value: BarcodeTemplate.A }, // Updated description
    { label: 'Şablon B - Ön Barkod, Arka Boş', value: BarcodeTemplate.B },
    { label: 'Şablon C - Ön Barkod, Arka Ürün Detayları', value: BarcodeTemplate.C },
    { label: 'Şablon D - Ön Barkod, Arka USD Fiyat', value: BarcodeTemplate.D },
    { label: 'Şablon E - Ön Barkod, Arka Barkod ID', value: BarcodeTemplate.E },
  ];

  // Handle template change
  const handleTemplateChange = (value: string) => {
    setSelectedTemplate(value as BarcodeTemplate);
    // Reset barcode ready state to force re-render
    setBarcodeReady(false);
    barcodeReadyRef.current = false;
  };

  // Remove getTemplateConfigScript and getSideBContentScript as they are no longer needed

  // Handle print action
  const handlePrint = () => {
    if (!barcodeReady || generatingSvg || !dataMatrixSvg) {
      return;
    }
    
    setIsPrinting(true);
    
    const printWindow = window.open('', '_blank');
    
    if (!printWindow) {
      alert('Lütfen popup engelleyiciyi devre dışı bırakın ve tekrar deneyin.');
      setIsPrinting(false);
      return;
    }
    
    // Generate HTML content using the utility function
    const labelHtml = generatePrintableLabelHtml(
      barcode,
      dataMatrixSvg, // SVG for Side A
      selectedTemplate,
      item,
      currentExchangeRate
    );

    // Write the HTML document with the script
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Barkod Yazdır: ${barcode}</title>
          <style>
            ${getStandardBarcodePrintStyles()}
            /* .data-matrix-container styles are in getStandardBarcodePrintStyles */
          </style>
        </head>
        <body>
          ${labelHtml}
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
                setTimeout(function() { window.close(); }, 500);
              }, 150);
            };
          </script>
        </body>
      </html>
    `);
    
    printWindow.document.close(); 
    
    setIsPrinting(false);
    onClose(); // Optionally close the modal after printing
  };

  // Log button state just before rendering
  const printButtonDisabled = isPrinting || !barcodeReady || generatingSvg || !dataMatrixSvg;

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title="Barkod Yazdır"
      size="medium"
      onSubmit={(e) => e.preventDefault()}
      hideButtons={true}
    >
      <div className="space-y-4">
        <FormField label="Barkod Şablonu" id="barcode-template-field">
          <SelectInput
            options={getTemplateOptions()}
            value={selectedTemplate}
            onChange={(e) => handleTemplateChange(e.target.value)}
            id="barcode-template"
            name="barcode-template"
          />
        </FormField>
        
        <div>
          <p className="text-sm text-gray-600 mb-2">Önizleme:</p>
          <div className="border p-2 inline-block bg-white barcode-label-container">
            <BarcodeLabel 
              barcode={barcode} 
              onLoad={handleBarcodeLoad}
              item={item}
              template={selectedTemplate}
            />
          </div>
        </div>
        
        {generatingSvg && (
          <p className="text-sm text-blue-600">Barkod oluşturuluyor...</p>
        )}
        {!generatingSvg && !dataMatrixSvg && barcode && (
           <p className="text-sm text-red-600">Barkod oluşturulamadı.</p>
        )}

        <div className="flex justify-end space-x-2">
          <Button variant={ButtonVariant.SECONDARY} onClick={onClose} disabled={isPrinting}>
            İptal
          </Button>
          <Button 
            variant={ButtonVariant.PRIMARY} 
            onClick={handlePrint} 
            disabled={printButtonDisabled}
          >
            {isPrinting ? 'Yazdırılıyor...' : 'Yazdır'}
          </Button>
        </div>
      </div>
    </FormModal>
  );
};

export default BarcodePrintModal; 